import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { API, getAuthToken } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  BookOpen, Plus, Edit, Trash2, Video, Eye, EyeOff, Users, Upload, 
  ArrowLeft, DollarSign, Gift, Crown, Play, Lock, Download,
  BarChart3, TrendingUp, CheckCircle, X, Search, Settings,
  Image as ImageIcon, Mail, User, FileText
} from 'lucide-react';

const AdminCoursesPage = () => {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [courses, setCourses] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('courses');
  
  // Course form state
  const [showCourseDialog, setShowCourseDialog] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [courseForm, setCourseForm] = useState({
    title: '',
    description: '',
    category: 'Other',
    is_free: true,
    price: 0,
    benefits: []
  });
  const [newBenefit, setNewBenefit] = useState('');
  const [thumbnailFile, setThumbnailFile] = useState(null);
  
  // Video management state
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [showVideoDialog, setShowVideoDialog] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [videoForm, setVideoForm] = useState({
    title: '',
    description: '',
    content_type: 'video',  // video or pdf
    is_preview: false,
    allow_download: false,
    order: 0
  });
  const [videoFile, setVideoFile] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [videoThumbnail, setVideoThumbnail] = useState(null);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  
  // Access grant state
  const [showGrantDialog, setShowGrantDialog] = useState(false);
  const [grantEmail, setGrantEmail] = useState('');
  const [grantReason, setGrantReason] = useState('');
  const [courseUsers, setCourseUsers] = useState([]);
  const [showUsersDialog, setShowUsersDialog] = useState(false);

  const categories = ['IT', 'Business', 'Marketing', 'Design', 'HR', 'Finance', 'Communication', 'Leadership', 'Other'];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const [userRes, coursesRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/auth/me`, config),
        axios.get(`${API}/courses`, config),
        axios.get(`${API}/admin/course-analytics`, config).catch(() => ({ data: null }))
      ]);

      if (userRes.data.role !== 'admin' && userRes.data.role !== 'user' && userRes.data.role !== 'crm_user') {
        navigate('/');
        return;
      }

      setCurrentUser(userRes.data);
      setCourses(coursesRes.data);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCourse = async () => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };

      let res;
      if (editingCourse) {
        res = await axios.patch(`${API}/courses/${editingCourse.id}`, courseForm, config);
        toast.success('Course updated!');
      } else {
        res = await axios.post(`${API}/courses`, courseForm, config);
        toast.success('Course created!');
      }

      // Upload thumbnail if selected
      if (thumbnailFile) {
        const courseId = editingCourse?.id || res.data.course?.id;
        if (courseId) {
          const formData = new FormData();
          formData.append('file', thumbnailFile);
          await axios.post(`${API}/courses/${courseId}/thumbnail`, formData, {
            headers: { 
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          });
        }
      }

      setShowCourseDialog(false);
      resetCourseForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save course');
    }
  };

  const handleDeleteCourse = async (courseId) => {
    if (!window.confirm('Are you sure you want to delete this course? All videos will also be deleted.')) return;
    
    try {
      const token = getAuthToken();
      await axios.delete(`${API}/courses/${courseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Course deleted');
      fetchData();
      if (selectedCourse?.id === courseId) {
        setSelectedCourse(null);
      }
    } catch (error) {
      toast.error('Failed to delete course');
    }
  };

  const handlePublishToggle = async (course) => {
    try {
      const token = getAuthToken();
      await axios.patch(`${API}/courses/${course.id}`, {
        is_published: !course.is_published
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(course.is_published ? 'Course unpublished' : 'Course published');
      fetchData();
    } catch (error) {
      toast.error('Failed to update course');
    }
  };

  const handleAddVideo = async () => {
    if (!selectedCourse) return;
    
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };

      let videoId;
      if (editingVideo) {
        await axios.patch(`${API}/courses/videos/${editingVideo.id}`, videoForm, config);
        videoId = editingVideo.id;
        toast.success('Content updated!');
      } else {
        const res = await axios.post(`${API}/courses/${selectedCourse.id}/videos`, videoForm, config);
        videoId = res.data.video?.id;
        toast.success('Content added!');
      }

      // Upload video file if selected
      if (videoFile && videoId && videoForm.content_type === 'video') {
        setUploadingVideo(true);
        const formData = new FormData();
        formData.append('file', videoFile);
        await axios.post(`${API}/courses/videos/${videoId}/upload`, formData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        toast.success('Video file uploaded!');
      }

      // Upload PDF file if selected
      if (pdfFile && videoId && videoForm.content_type === 'pdf') {
        setUploadingVideo(true);
        const formData = new FormData();
        formData.append('file', pdfFile);
        await axios.post(`${API}/courses/videos/${videoId}/upload-pdf`, formData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        toast.success('PDF file uploaded!');
      }

      // Upload video thumbnail if selected
      if (videoThumbnail && videoId) {
        const formData = new FormData();
        formData.append('file', videoThumbnail);
        await axios.post(`${API}/courses/videos/${videoId}/thumbnail`, formData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
      }

      setShowVideoDialog(false);
      resetVideoForm();
      fetchCourseDetails(selectedCourse.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save video');
    } finally {
      setUploadingVideo(false);
    }
  };

  const handleDeleteVideo = async (videoId) => {
    if (!window.confirm('Are you sure you want to delete this video?')) return;
    
    try {
      const token = getAuthToken();
      await axios.delete(`${API}/courses/videos/${videoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Video deleted');
      fetchCourseDetails(selectedCourse.id);
    } catch (error) {
      toast.error('Failed to delete video');
    }
  };

  const handleGrantAccess = async () => {
    if (!selectedCourse || !grantEmail) return;
    
    try {
      const token = getAuthToken();
      await axios.post(
        `${API}/courses/${selectedCourse.id}/grant-access?user_email=${encodeURIComponent(grantEmail)}&reason=${encodeURIComponent(grantReason)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Access granted to ${grantEmail}`);
      setShowGrantDialog(false);
      setGrantEmail('');
      setGrantReason('');
      fetchCourseUsers(selectedCourse.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to grant access');
    }
  };

  const handleRevokeAccess = async (userEmail) => {
    if (!window.confirm(`Revoke access for ${userEmail}?`)) return;
    
    try {
      const token = getAuthToken();
      await axios.delete(`${API}/courses/${selectedCourse.id}/revoke-access/${encodeURIComponent(userEmail)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Access revoked');
      fetchCourseUsers(selectedCourse.id);
    } catch (error) {
      toast.error('Failed to revoke access');
    }
  };

  const fetchCourseDetails = async (courseId) => {
    try {
      const token = getAuthToken();
      const res = await axios.get(`${API}/courses/${courseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedCourse(res.data);
    } catch (error) {
      toast.error('Failed to load course details');
    }
  };

  const fetchCourseUsers = async (courseId) => {
    try {
      const token = getAuthToken();
      const res = await axios.get(`${API}/courses/${courseId}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCourseUsers(res.data);
    } catch (error) {
      toast.error('Failed to load users');
    }
  };

  const resetCourseForm = () => {
    setCourseForm({
      title: '',
      description: '',
      category: 'Other',
      is_free: true,
      price: 0,
      benefits: []
    });
    setEditingCourse(null);
    setThumbnailFile(null);
    setNewBenefit('');
  };

  const resetVideoForm = () => {
    setVideoForm({
      title: '',
      description: '',
      content_type: 'video',
      is_preview: false,
      allow_download: false,
      order: 0
    });
    setEditingVideo(null);
    setVideoFile(null);
    setPdfFile(null);
    setVideoThumbnail(null);
  };

  const openEditCourse = (course) => {
    setEditingCourse(course);
    setCourseForm({
      title: course.title,
      description: course.description,
      category: course.category,
      is_free: course.is_free,
      price: course.price,
      benefits: course.benefits || []
    });
    setShowCourseDialog(true);
  };

  const openEditVideo = (video) => {
    setEditingVideo(video);
    setVideoForm({
      title: video.title,
      description: video.description || '',
      content_type: video.content_type || 'video',
      is_preview: video.is_preview,
      allow_download: video.allow_download,
      order: video.order
    });
    setShowVideoDialog(true);
  };

  const addBenefit = () => {
    if (newBenefit.trim()) {
      setCourseForm(prev => ({
        ...prev,
        benefits: [...prev.benefits, newBenefit.trim()]
      }));
      setNewBenefit('');
    }
  };

  const removeBenefit = (index) => {
    setCourseForm(prev => ({
      ...prev,
      benefits: prev.benefits.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Course Dialog */}
      <Dialog open={showCourseDialog} onOpenChange={setShowCourseDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingCourse ? 'Edit Course' : 'Create New Course'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Title *</Label>
              <Input
                value={courseForm.title}
                onChange={(e) => setCourseForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Enter course title"
              />
            </div>
            <div>
              <Label>Description *</Label>
              <Textarea
                value={courseForm.description}
                onChange={(e) => setCourseForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Enter course description"
                rows={4}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select 
                  value={courseForm.category} 
                  onValueChange={(v) => setCourseForm(prev => ({ ...prev, category: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Course Type</Label>
                <Select 
                  value={courseForm.is_free ? 'free' : 'paid'} 
                  onValueChange={(v) => setCourseForm(prev => ({ 
                    ...prev, 
                    is_free: v === 'free',
                    price: v === 'free' ? 0 : prev.price
                  }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="free">Free Course</SelectItem>
                    <SelectItem value="paid">Paid Course</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {!courseForm.is_free && (
              <div>
                <Label>Price (₹)</Label>
                <Input
                  type="number"
                  value={courseForm.price}
                  onChange={(e) => setCourseForm(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                  placeholder="Enter price"
                />
              </div>
            )}
            <div>
              <Label>Thumbnail Image</Label>
              <Input
                type="file"
                accept="image/*"
                onChange={(e) => setThumbnailFile(e.target.files[0])}
              />
            </div>
            <div>
              <Label>Benefits (What students will learn)</Label>
              <div className="flex gap-2 mb-2">
                <Input
                  value={newBenefit}
                  onChange={(e) => setNewBenefit(e.target.value)}
                  placeholder="Add a benefit"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addBenefit())}
                />
                <Button type="button" onClick={addBenefit} size="sm">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <div className="space-y-2">
                {courseForm.benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                    <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                    <span className="flex-1 text-sm">{benefit}</span>
                    <button onClick={() => removeBenefit(index)} className="text-red-500 hover:text-red-700">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCourseDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateCourse} disabled={!courseForm.title || !courseForm.description}>
              {editingCourse ? 'Update Course' : 'Create Course'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Video Dialog */}
      <Dialog open={showVideoDialog} onOpenChange={setShowVideoDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingVideo ? 'Edit Content' : 'Add New Content'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Content Type *</Label>
              <Select 
                value={videoForm.content_type} 
                onValueChange={(v) => setVideoForm(prev => ({ ...prev, content_type: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="video">
                    <div className="flex items-center gap-2">
                      <Video className="h-4 w-4" />
                      Video
                    </div>
                  </SelectItem>
                  <SelectItem value="pdf">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      PDF Document
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Title *</Label>
              <Input
                value={videoForm.title}
                onChange={(e) => setVideoForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder={videoForm.content_type === 'pdf' ? "Enter document title" : "Enter video title"}
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={videoForm.description}
                onChange={(e) => setVideoForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Enter description"
                rows={3}
              />
            </div>
            <div>
              <Label>Order (position in list)</Label>
              <Input
                type="number"
                value={videoForm.order}
                onChange={(e) => setVideoForm(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
              />
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={videoForm.is_preview}
                  onCheckedChange={(checked) => setVideoForm(prev => ({ ...prev, is_preview: checked }))}
                />
                <Label className="font-normal">Preview (viewable without purchase)</Label>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                checked={videoForm.allow_download}
                onCheckedChange={(checked) => setVideoForm(prev => ({ ...prev, allow_download: checked }))}
              />
              <Label className="font-normal">Allow Download</Label>
            </div>
            
            {videoForm.content_type === 'video' && (
              <>
                <div>
                  <Label>Video File</Label>
                  <Input
                    type="file"
                    accept="video/*"
                    onChange={(e) => setVideoFile(e.target.files[0])}
                  />
                  {videoFile && (
                    <p className="text-sm text-gray-500 mt-1">
                      Selected: {videoFile.name} ({(videoFile.size / 1024 / 1024).toFixed(2)} MB)
                    </p>
                  )}
                </div>
                <div>
                  <Label>Video Thumbnail</Label>
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setVideoThumbnail(e.target.files[0])}
                  />
                </div>
              </>
            )}
            
            {videoForm.content_type === 'pdf' && (
              <div>
                <Label>PDF File</Label>
                <Input
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={(e) => setPdfFile(e.target.files[0])}
                />
                {pdfFile && (
                  <p className="text-sm text-gray-500 mt-1">
                    Selected: {pdfFile.name} ({(pdfFile.size / 1024 / 1024).toFixed(2)} MB)
                  </p>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVideoDialog(false)}>Cancel</Button>
            <Button onClick={handleAddVideo} disabled={!videoForm.title || uploadingVideo}>
              {uploadingVideo ? 'Uploading...' : (editingVideo ? 'Update' : 'Add')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Grant Access Dialog */}
      <Dialog open={showGrantDialog} onOpenChange={setShowGrantDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Grant Course Access</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>User Email *</Label>
              <Input
                type="email"
                value={grantEmail}
                onChange={(e) => setGrantEmail(e.target.value)}
                placeholder="Enter user email"
              />
            </div>
            <div>
              <Label>Reason (optional)</Label>
              <Input
                value={grantReason}
                onChange={(e) => setGrantReason(e.target.value)}
                placeholder="e.g., Contest winner, Promotional access"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGrantDialog(false)}>Cancel</Button>
            <Button onClick={handleGrantAccess} disabled={!grantEmail}>
              Grant Access
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Course Users Dialog */}
      <Dialog open={showUsersDialog} onOpenChange={setShowUsersDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Users with Access - {selectedCourse?.title}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {courseUsers.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No users have access yet</p>
            ) : (
              <div className="space-y-3">
                {courseUsers.map((user, index) => (
                  <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <p className="font-medium">{user.name || user.email}</p>
                      <p className="text-sm text-gray-500">{user.email}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge className={
                          user.access_type === 'purchased' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                        }>
                          {user.access_type === 'purchased' ? 'Purchased' : 'Granted'}
                        </Badge>
                        {user.access_type === 'granted' && user.granted_by && (
                          <span className="text-xs text-gray-400">by {user.granted_by}</span>
                        )}
                      </div>
                    </div>
                    {user.access_type === 'granted' && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => handleRevokeAccess(user.email)}
                      >
                        Revoke
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Header */}
      <header className="bg-gradient-to-r from-violet-600 to-purple-600 text-white">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button 
                onClick={() => navigate('/')}
                className="p-2 hover:bg-white/10 rounded-full"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold">Course Management</h1>
                <p className="text-white/70 text-sm">Manage your courses and videos</p>
              </div>
            </div>
            <Button 
              onClick={() => {
                resetCourseForm();
                setShowCourseDialog(true);
              }}
              className="bg-white text-violet-600 hover:bg-white/90"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Course
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="courses" className="flex items-center gap-2">
              <BookOpen className="h-4 w-4" />
              Courses
            </TabsTrigger>
            <TabsTrigger value="analytics" className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Analytics
            </TabsTrigger>
          </TabsList>

          <TabsContent value="courses">
            {selectedCourse ? (
              // Course Detail / Video Management View
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <button 
                    onClick={() => setSelectedCourse(null)}
                    className="flex items-center gap-2 text-violet-600 hover:text-violet-700"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Back to Courses
                  </button>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => {
                        fetchCourseUsers(selectedCourse.id);
                        setShowUsersDialog(true);
                      }}
                    >
                      <Users className="h-4 w-4 mr-2" />
                      View Users
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setShowGrantDialog(true)}
                    >
                      <Mail className="h-4 w-4 mr-2" />
                      Grant Access
                    </Button>
                    <Button onClick={() => {
                      resetVideoForm();
                      setVideoForm(prev => ({ ...prev, order: (selectedCourse.videos?.length || 0) + 1 }));
                      setShowVideoDialog(true);
                    }}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Video
                    </Button>
                  </div>
                </div>

                {/* Course Info Card */}
                <Card>
                  <CardContent className="p-6">
                    <div className="flex gap-6">
                      {selectedCourse.thumbnail_id ? (
                        <img 
                          src={`${API}/courses/thumbnail/${selectedCourse.thumbnail_id}`}
                          alt={selectedCourse.title}
                          className="w-48 h-32 object-cover rounded-xl"
                        />
                      ) : (
                        <div className="w-48 h-32 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center">
                          <BookOpen className="h-12 w-12 text-white/50" />
                        </div>
                      )}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className={selectedCourse.is_free ? "bg-green-100 text-green-700" : "bg-violet-100 text-violet-700"}>
                            {selectedCourse.is_free ? 'Free' : `₹${selectedCourse.price}`}
                          </Badge>
                          <Badge variant="outline">{selectedCourse.category}</Badge>
                          <Badge className={selectedCourse.is_published ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}>
                            {selectedCourse.is_published ? 'Published' : 'Draft'}
                          </Badge>
                        </div>
                        <h2 className="text-xl font-bold text-slate-800 mb-2">{selectedCourse.title}</h2>
                        <p className="text-gray-600 text-sm mb-3">{selectedCourse.description}</p>
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <Video className="h-4 w-4" />
                            {selectedCourse.total_videos} videos
                          </span>
                          <span className="flex items-center gap-1">
                            <Eye className="h-4 w-4" />
                            {selectedCourse.total_views} views
                          </span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Videos List */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Video className="h-5 w-5" />
                      Course Content
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {selectedCourse.videos?.length > 0 ? (
                      <div className="space-y-3">
                        {selectedCourse.videos.map((video, index) => (
                          <div 
                            key={video.id}
                            className="flex items-center gap-4 p-4 border rounded-xl hover:bg-gray-50"
                          >
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-semibold ${
                              video.content_type === 'pdf' 
                                ? 'bg-red-100 text-red-600' 
                                : 'bg-violet-100 text-violet-600'
                            }`}>
                              {video.content_type === 'pdf' ? (
                                <FileText className="h-5 w-5" />
                              ) : (
                                index + 1
                              )}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <h4 className="font-medium">{video.title}</h4>
                                <Badge className={
                                  video.content_type === 'pdf' 
                                    ? 'bg-red-100 text-red-700 text-xs' 
                                    : 'bg-violet-100 text-violet-700 text-xs'
                                }>
                                  {video.content_type === 'pdf' ? 'PDF' : 'Video'}
                                </Badge>
                                {video.is_preview && (
                                  <Badge className="bg-green-100 text-green-700 text-xs">Preview</Badge>
                                )}
                                {video.allow_download && (
                                  <Badge className="bg-blue-100 text-blue-700 text-xs">
                                    <Download className="h-3 w-3 mr-1" />
                                    Download
                                  </Badge>
                                )}
                              </div>
                              {video.description && (
                                <p className="text-sm text-gray-500 mt-1">{video.description}</p>
                              )}
                              <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                                <span className="flex items-center gap-1">
                                  <Eye className="h-3 w-3" />
                                  {video.views} views
                                </span>
                                {video.video_id && (
                                  <span className="text-green-600">Video uploaded</span>
                                )}
                                {video.pdf_id && (
                                  <span className="text-red-600">PDF uploaded</span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditVideo(video)}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleDeleteVideo(video.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <Video className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500">No content yet</p>
                        <Button 
                          className="mt-4"
                          onClick={() => {
                            resetVideoForm();
                            setShowVideoDialog(true);
                          }}
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add First Video
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            ) : (
              // Course Listing View
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {courses.length === 0 ? (
                  <div className="col-span-full text-center py-16">
                    <BookOpen className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-600">No courses yet</h3>
                    <p className="text-gray-500 mt-2">Create your first course to get started</p>
                    <Button 
                      className="mt-4"
                      onClick={() => {
                        resetCourseForm();
                        setShowCourseDialog(true);
                      }}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Create Course
                    </Button>
                  </div>
                ) : (
                  courses.map(course => (
                    <Card 
                      key={course.id}
                      className="overflow-hidden hover:shadow-lg transition-shadow"
                    >
                      <div className="relative">
                        {course.thumbnail_id ? (
                          <img 
                            src={`${API}/courses/thumbnail/${course.thumbnail_id}`}
                            alt={course.title}
                            className="w-full h-40 object-cover"
                          />
                        ) : (
                          <div className="w-full h-40 bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                            <BookOpen className="h-12 w-12 text-white/30" />
                          </div>
                        )}
                        <div className="absolute top-3 left-3 flex gap-2">
                          <Badge className={course.is_free ? "bg-green-500 text-white" : "bg-violet-600 text-white"}>
                            {course.is_free ? 'Free' : `₹${course.price}`}
                          </Badge>
                        </div>
                        <div className="absolute top-3 right-3">
                          <Badge className={course.is_published ? "bg-green-500 text-white" : "bg-yellow-500 text-white"}>
                            {course.is_published ? 'Live' : 'Draft'}
                          </Badge>
                        </div>
                      </div>
                      <CardContent className="p-4">
                        <Badge variant="outline" className="mb-2">{course.category}</Badge>
                        <h3 className="font-semibold text-slate-800 mb-2 line-clamp-2">{course.title}</h3>
                        <p className="text-sm text-gray-500 line-clamp-2 mb-3">{course.description}</p>
                        <div className="flex items-center gap-4 text-sm text-gray-400 mb-4">
                          <span className="flex items-center gap-1">
                            <Video className="h-4 w-4" />
                            {course.total_videos}
                          </span>
                          <span className="flex items-center gap-1">
                            <Eye className="h-4 w-4" />
                            {course.total_views || 0}
                          </span>
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="flex-1"
                            onClick={() => fetchCourseDetails(course.id)}
                          >
                            Manage
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEditCourse(course)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant={course.is_published ? "outline" : "secondary"}
                            size="sm"
                            className={course.is_published ? "text-green-600 border-green-300" : "text-gray-600"}
                            onClick={() => handlePublishToggle(course)}
                            title={course.is_published ? "Click to unpublish" : "Click to publish"}
                          >
                            {course.is_published ? (
                              <>
                                <Eye className="h-4 w-4 mr-1" />
                                Live
                              </>
                            ) : (
                              <>
                                <EyeOff className="h-4 w-4 mr-1" />
                                Draft
                              </>
                            )}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:bg-red-50"
                            onClick={() => handleDeleteCourse(course.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            )}
          </TabsContent>

          <TabsContent value="analytics">
            {analytics && (
              <div className="space-y-6">
                {/* Stats Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="p-3 bg-violet-100 rounded-xl">
                          <BookOpen className="h-6 w-6 text-violet-600" />
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Total Courses</p>
                          <p className="text-2xl font-bold">{analytics.total_courses}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="p-3 bg-blue-100 rounded-xl">
                          <Video className="h-6 w-6 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Total Videos</p>
                          <p className="text-2xl font-bold">{analytics.total_videos}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="p-3 bg-green-100 rounded-xl">
                          <Eye className="h-6 w-6 text-green-600" />
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Total Views</p>
                          <p className="text-2xl font-bold">{analytics.total_views}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="p-3 bg-amber-100 rounded-xl">
                          <DollarSign className="h-6 w-6 text-amber-600" />
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Total Revenue</p>
                          <p className="text-2xl font-bold">₹{analytics.total_revenue}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Top Courses */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="h-5 w-5" />
                      Top Courses by Views
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {analytics.top_courses?.length > 0 ? (
                      <div className="space-y-3">
                        {analytics.top_courses.map((course, index) => (
                          <div key={course.id} className="flex items-center gap-4 p-3 border rounded-lg">
                            <div className="w-8 h-8 bg-violet-100 rounded-full flex items-center justify-center text-violet-600 font-bold">
                              {index + 1}
                            </div>
                            <div className="flex-1">
                              <h4 className="font-medium">{course.title}</h4>
                              <div className="flex items-center gap-4 text-sm text-gray-500">
                                <span>{course.views} views</span>
                                <span>{course.purchases} purchases</span>
                                <Badge className={course.is_free ? "bg-green-100 text-green-700" : "bg-violet-100 text-violet-700"}>
                                  {course.is_free ? 'Free' : 'Paid'}
                                </Badge>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-gray-500 py-8">No data yet</p>
                    )}
                  </CardContent>
                </Card>

                {/* Recent Purchases */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="h-5 w-5" />
                      Recent Purchases
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {analytics.recent_purchases?.length > 0 ? (
                      <div className="space-y-3">
                        {analytics.recent_purchases.map((purchase) => (
                          <div key={purchase.id} className="flex items-center justify-between p-3 border rounded-lg">
                            <div>
                              <p className="font-medium">{purchase.user_name}</p>
                              <p className="text-sm text-gray-500">{purchase.user_email}</p>
                              <p className="text-sm text-gray-400">
                                Purchased: {purchase.course_title}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-green-600">₹{purchase.amount}</p>
                              <p className="text-xs text-gray-400">
                                {purchase.purchased_at && format(new Date(purchase.purchased_at), 'MMM dd, yyyy')}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-gray-500 py-8">No purchases yet</p>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AdminCoursesPage;
